import React, { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { APP_CONFIG } from '../../../config/app.config';

interface FileDragDropProps {
  children?: React.ReactNode;
  onFileSelect: (file: File) => void;
  allowedFileTypes?: string[];
  maxFileSize?: number; // in bytes
  disabled?: boolean;
}

const FileDragDrop: React.FC<FileDragDropProps> = ({
  children,
  onFileSelect,
  allowedFileTypes,
  maxFileSize,
  disabled = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragError, setDragError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    if (maxFileSize && file.size > maxFileSize) {
      const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
      return `File too large. Maximum size: ${maxSizeMB}MB`;
    }

    return null;
  };

  const handleFile = (file: File) => {
    const error = validateFile(file);
    if (error) {
      setDragError(error);
      setTimeout(() => setDragError(null), 3000);
      return;
    }

    setDragError(null);
    onFileSelect(file);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed',
          borderColor: isDragOver 
            ? APP_CONFIG.ui.theme.colors.primary 
            : disabled 
              ? 'grey.300' 
              : 'grey.300',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          backgroundColor: isDragOver 
            ? `${APP_CONFIG.ui.theme.colors.primary}10` 
            : 'transparent',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: disabled ? 'grey.300' : APP_CONFIG.ui.theme.colors.primary,
            backgroundColor: disabled ? 'transparent' : `${APP_CONFIG.ui.theme.colors.primary}05`,
          },
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {children || (
          <Box>
            <UploadFile
              sx={{ 
                color: isDragOver 
                  ? APP_CONFIG.ui.theme.colors.primary 
                  : disabled 
                    ? 'grey.400' 
                    : APP_CONFIG.ui.theme.colors.primary,
                fontSize: 48,
                mb: 2,
              }} 
            />
            <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
              {isDragOver ? 'Drop your file here' : 'Upload file'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag and drop a file here, or click to select
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Allowed types: {allowedFileTypes?.join(', ')}
            </Typography>
          </Box>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedFileTypes?.join(',')}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />
      </Box>

      {/* Error message */}
      {dragError && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            p: 2,
            backgroundColor: 'error.light',
            color: 'error.contrastText',
            borderRadius: 1,
            fontSize: '0.875rem',
            zIndex: 1,
          }}
        >
          {dragError}
        </Box>
      )}
    </Box>
  );
};

export default FileDragDrop; 